import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import caseService from '../../services/case.service';
import PageHeader from '../../components/layout/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import Spinner from '../../components/ui/Spinner';
import Table, { type TableColumn } from '../../components/ui/Table';
import { useDebounce } from '../../hooks/useDebounce';
import { QrCode, Printer } from 'lucide-react';
import QRCodeModal from '../../components/features/cases/QRCodeModal';
import { printCaseQrCode } from '../../utils/qrCodePrint';
import type { CaseSummary } from '../../types/case.types';

const CaseListPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [qrCase, setQrCase] = useState<CaseSummary | null>(null);
  const [printingCaseId, setPrintingCaseId] = useState<string | null>(null);

  const handlePrintQr = async (item: CaseSummary) => {
    setPrintingCaseId(item.case_id);
    try {
      await printCaseQrCode(item);
    } catch {
      toast.error('Failed to generate QR code for printing.');
    } finally {
      setPrintingCaseId(null);
    }
  };

  const query = useQuery({
    queryKey: ['cases', { page, search: debouncedSearch }],
    queryFn: () =>
      caseService.searchCases({
        q: debouncedSearch || undefined,
        page,
        limit: 10,
      }),
    select: (res) => res.data?.data,
  });

  useEffect(() => {
    document.title = 'RRMS | Cases';
  }, []);

  useEffect(() => {
    if (query.error) {
      const message = (query.error as any)?.response?.data?.message;
      toast.error(message || 'Failed to load cases.');
    }
  }, [query.error]);

  const data = query.data as
    | {
        items: CaseSummary[];
        pagination?: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNextPage?: boolean;
          hasPrevPage?: boolean;
        };
      }
    | undefined;

  const items: CaseSummary[] = data?.items ?? [];
  const pagination =
    data?.pagination ?? ({
      page,
      limit: 20,
      total: items.length,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    } as const);

  // Columns follow the upload-form redesign: File No / Year / hierarchy
  // (Section path from the admin master) / Category / Subject.
  const columns: TableColumn<CaseSummary>[] = [
    {
      key: 'fir_number',
      header: 'File No',
      minWidth: 130,
      clampLines: 2,
      tooltip: (item) => item.fir_number,
      cellStyle: {
        fontWeight: 600,
        color: 'var(--color-primary)',
      },
      render: (item) => item.fir_number || (item as any).cms_number || '—',
    },
    {
      key: 'year',
      header: 'Year',
      minWidth: 80,
      tooltip: (item) => String((item as any).year ?? ''),
      render: (item) => (item as any).year ?? '—',
    },
    {
      key: 'hierarchy_path',
      header: 'Section',
      minWidth: 200,
      clampLines: 2,
      tooltip: (item) => (item as any).hierarchy_path ?? '',
      render: (item) => (item as any).hierarchy_path || '—',
    },
    {
      key: 'category',
      header: 'Category',
      minWidth: 130,
      clampLines: 2,
      tooltip: (item) => (item as any).category ?? '',
      render: (item) => (item as any).category || '—',
    },
    {
      key: 'subject',
      header: 'Subject',
      minWidth: 220,
      clampLines: 2,
      tooltip: (item) => (item as any).subject ?? '',
      render: (item) => (item as any).subject || '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 210,
      tooltip: () => 'View Documents',
      render: (item) => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => navigate(`/cases/${encodeURIComponent(item.case_id)}`)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '5px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-primary)',
              background: 'transparent',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-primary)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--color-primary)';
            }}
          >
            View Documents
          </button>
          <button
            type="button"
            title="Show QR code"
            onClick={() => setQrCase(item)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '5px 8px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-primary)',
              background: 'transparent',
              color: 'var(--color-primary)',
              cursor: 'pointer',
            }}
          >
            <QrCode size={15} />
          </button>
          <button
            type="button"
            title="Print QR code"
            onClick={() => handlePrintQr(item)}
            disabled={printingCaseId === item.case_id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '5px 8px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-primary)',
              background: 'transparent',
              color: 'var(--color-primary)',
              cursor: printingCaseId === item.case_id ? 'not-allowed' : 'pointer',
              opacity: printingCaseId === item.case_id ? 0.5 : 1,
            }}
          >
            <Printer size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Cases"
        subtitle="Search and manage RRMS cases."
        actions={
          <input
            type="text"
            placeholder="Search by CMS number, FIR, or ref number..."
            className="w-full md:w-80 rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-primary-light)]"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        }
      />

      {query.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner size={20} />
        </div>
      ) : query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : !items.length ? (
        <EmptyState
          title="No cases found"
          description="Try adjusting your search or filters."
        />
      ) : (
        <Table
          columns={columns}
          rows={items}
          rowKey={(item) => item.case_id}
          pagination={pagination}
          onPageChange={setPage}
        />
      )}

      {qrCase && (
        <QRCodeModal caseItem={qrCase} onClose={() => setQrCase(null)} />
      )}
    </div>
  );
};

export default CaseListPage;
